/**
 * General top-level specific lookup of current session information goes here.
 */
import { createSelector } from "reselect";
import { JupyterSource } from "../../session/JupyterSource";
import {
  KernelLegacyStatus,
  KernelLegacyStatusName,
} from "../../telemetry/kernelTelemetry";
import { EditorStatus } from "../../types/EditorStatus";
import { AppState } from "../types";

// TODO(cochamberlain): We need to rethink our redux structure. "notebook" represents
// the current interface state and is not applicable to scripts. If everything becomes
// a notebook then it makes a bit more sense but then we need a different term to
// represent "not a script". I think current or session would be better names for this.
/** Gets the current state of the session. */
export const getCurrent = (state: AppState) => {
  return state.notebook;
};

/** Selects the current editor object from Redux. */
export const getCurrentEditor = (state: AppState) => {
  return state.editor;
};

/** Selects the current kernel id. */
export const getCurrentKernelId = createSelector(
  [getCurrent],
  current => (current && current.kernel) || null
);

/** Selects the current kernel run id. */
export const getCurrentKernelRunId = createSelector(
  [getCurrent],
  current => current.run
);

/** Selects the current draft sequence from redux. */
export const getCurrentSequence = createSelector([getCurrent], current =>
  current ? current.sequence : null
);

/** Selects the current ipynb from Redux. */
export const getCurrentIpynb = createSelector(
  [getCurrent],
  current => (current && current.ipynb) || null
);

/** Gets the JupyterSource wrapping current redux tracked ipynb. */
export const getCurrentJupyterSource = createSelector(
  [getCurrentIpynb],
  ipynb => (ipynb === null ? null : new JupyterSource(ipynb))
);

/** Gets the JupyterSource toNotebookContent() result for redux tracked ipynb. */
export const getCurrentNotebookContent = createSelector(
  [getCurrentJupyterSource],
  source => (source === null ? null : source.toNotebookContent())
);

/** Selects an array of serialized cells. */
export const getCurrentIpynbCells = createSelector(
  [getCurrentNotebookContent],
  content => (content === null ? [] : content.cells || [])
);

/** Selects the current title. */
export const getCurrentTitle = createSelector(
  [getCurrent],
  current => (current && current.title) || null
);

/** Selects the current slug. */
export const getCurrentSlug = createSelector(
  [getCurrent],
  current => (current && current.slug) || null
);

/** Selects the current Docker image tag. */
export const getCurrentDockerImageTag = createSelector(
  [getCurrent],
  current => (current && current.dockerImageTag) || null
);

/** Selects the current worker pool name. */
export const getCurrentWorkerPoolName = createSelector(
  [getCurrent],
  current => (current && current.workerPoolName) || null
);

/** Selects the current data sources array. */
export const getCurrentDataSourceReferences = createSelector(
  [getCurrent],
  current => (current && current.dataSourceReferences) || []
);

/** Selects the current data sources array. */
export const getCurrentDataSources = createSelector(
  [getCurrent],
  current => (current && current.dataSources) || []
);

/** Selects whether Internet setting is currently enabled. */
export const getCurrentInternetEnabled = createSelector(
  [getCurrent],
  current => (current && current.internet) || false
);

/** Selects the unit name running this session. */
export const getBackendUnitName = createSelector(
  [getCurrent],
  current => (current && current.unitName) || "unknown"
);

/** Selects which accelerator setting is currently enabled. */
export const getCurrentAccelerator = createSelector(
  [getCurrent],
  current => (current && current.accelerator) || "none"
);

/** Selects the current editor status in Redux. */
export const getCurrentEditorStatus = createSelector(
  [getCurrentEditor],
  editor => editor.status
);

/** Returns the CellType of the currently selected cell. If multiple cells with different types, return 'raw'. */
export const getSelectedCellType = createSelector(
  [getCurrentEditor],
  editor => editor.selectedCellType
);

/** Returns true if the current cell will be hidden when shown in the notebook viewer. If multiple cells with different types, return null. */
export const getSelectedCellHidden = createSelector(
  [getCurrentEditor],
  editor => editor.selectedCellHidden
);

/** Returns true if the current cells output will be hidden when shown in the notebook viewer. If multiple cells with different types, return null. */
export const getSelectedCellOutputHidden = createSelector(
  [getCurrentEditor],
  editor => editor.selectedCellOutputHidden
);

/** Selects the legacy status description values used in the prior editor. */
export const getCurrentLegacyStatusName = createSelector(
  [getCurrentEditorStatus],
  (status): KernelLegacyStatusName => {
    switch (status) {
      case EditorStatus.FATAL:
        return "ERROR";
      default:
        return status;
    }
  }
);

/** Selects the legacy status enum values used in the prior editor. */
export const getCurrentLegacyStatusId = createSelector(
  [getCurrentLegacyStatusName],
  statusName => KernelLegacyStatus[statusName]
);

/** Selects the current language id. */
export const getCurrentLanguageId = createSelector(
  [getCurrent],
  current => (current && current.languageId) || null
);

/** Selects the current route path. */
export const getCurrentRoute = createSelector(
  [getCurrent],
  current => current && current.kernelIdPath
);

/** Selects the last removed datasource. */
export const getCurrentRemovedDataSource = createSelector(
  [getCurrent],
  current => current && current.dataSourceRemoved
);
